/**
 * CLIENT ENVIRONMENT AWARENESS.
 *
 * The server tells the client which deployment it is via GET /api/config → { env, paymentsEnabled }.
 * `env` is "production" (live) or "staging" (the test server). We fetch it once and cache it so the UI
 * can show an unmistakable "TEST SERVER" banner when you're on staging — you should never confuse the
 * two while testing. Defaults to "production" if the config can't be read (fail safe: never falsely
 * label live as test).
 *
 * It is ALSO the first clock sync of the session. The response carries the server's `now`, which is fed
 * to `setServerNow()` so `gameNow()` is anchored to the server rather than the device before any growth
 * is computed. This route in particular, because anonymous play never calls /api/save — without the
 * config sync those sessions would run entirely on an unchecked device clock. See sim/gameClock.
 */
import { setServerNow, gameNow } from "./gameClock";
import { coerceReleaseStates, setReleaseConfig, setReleaseClock, type ReleaseState } from "./releases";

// content releases are judged by the server-synced clock (sim/releases.ts)
setReleaseClock(gameNow);
import { coerceKickstarter, KICKSTARTER_OFF, type KickstarterConfig } from "./kickstarter";
import { coercePrelaunch, PRELAUNCH_OFF, type PrelaunchConfig } from "./prelaunch";

export type AppEnv = "production" | "staging";

/** `kickstarter` is the demo mode (sim/kickstarter.ts), `prelaunch` the landing-page-only mode (sim/prelaunch.ts).
 *  `fromServer` is false when every attempt to read /api/config failed and this is the fallback — a guess, not
 *  an answer: nothing may treat its "off" as a fact (App holds the demo's held XP until a real answer says off). */
export interface AppConfig {
  env: AppEnv; paymentsEnabled: boolean; maintenance: boolean; kickstarter: KickstarterConfig; prelaunch: PrelaunchConfig;
  /** content release states (sim/releases.ts) — also pushed into the release gate on every read */
  releases: Record<string, ReleaseState>;
  fromServer: boolean;
}

let _cache: AppConfig | null = null;
let _inflight: Promise<AppConfig> | null = null;

const FALLBACK = (): AppConfig => ({ env: "production", paymentsEnabled: false, maintenance: false,
  kickstarter: { ...KICKSTARTER_OFF }, prelaunch: { ...PRELAUNCH_OFF }, releases: {}, fromServer: false });

/** One uncached read of /api/config; null on any failure. Re-syncs the game clock as a side effect. */
async function readConfig(): Promise<AppConfig | null> {
  const sentAt = Date.now();
  try {
    const r = await fetch("/api/config", { cache: "no-store" });
    if (!r.ok) return null;
    const j = await r.json();
    setServerNow(j.now, sentAt);
    const env: AppEnv = j.env === "staging" ? "staging" : "production";
    const releases = coerceReleaseStates(j.releases);
    // THE RELEASE GATE follows every read (boot and the minute poll): a release switched on, off or rescheduled in the
    // admin console reaches every open game within a minute, and a scheduled one at its moment (sim/releases.ts)
    setReleaseConfig(releases, env);
    return { env, paymentsEnabled: !!j.paymentsEnabled, maintenance: !!j.maintenance,
      kickstarter: coerceKickstarter(j.kickstarter), prelaunch: coercePrelaunch(j.prelaunch), releases, fromServer: true };
  } catch { return null; }
}

/**
 * Fetch (and cache) the runtime config from the server. Safe to call repeatedly. Note: `maintenance` in the cached
 * copy is a boot-time snapshot; use `fetchMaintenance()` for a live re-check, and `watchLaunchModes` to follow the
 * launch switches.
 *
 * A FAILURE IS NEVER CACHED. It used to be: one failed read at boot (a blip, a 429 on a busy shared address, a
 * blocker) cached "Kickstarter off, pre-launch off" for the life of the tab — an uncapped demo session with no
 * notice. Now it retries with backoff, and if every attempt fails it answers with the fallback (`fromServer:
 * false`) WITHOUT caching it, so the next caller — and the launch-mode watcher — asks again.
 */
export async function getAppConfig(): Promise<AppConfig> {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    for (const wait of [0, 600, 1800]) {
      if (wait) await new Promise((r) => setTimeout(r, wait));
      const c = await readConfig();
      if (c) { _cache = c; return c; }
    }
    return FALLBACK();
  })().finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * FOLLOW THE LAUNCH SWITCHES in an open tab. The demo's two modes used to be read once per page load, so a tab
 * open when an admin flipped one kept the old mode until it reloaded — still uncapped after Kickstarter mode went
 * on, still on the landing page after pre-launch went off. This re-reads /api/config every `everyMs` and when the
 * tab becomes visible again, refreshes the cache, and calls `onChange` whenever Kickstarter mode or pre-launch mode
 * differs from what the caller last saw (including the first real answer after a failed boot). Returns a stop fn.
 */
export function watchLaunchModes(onChange: (cfg: AppConfig) => void, everyMs = 60_000): () => void {
  let last = _cache ? stampOf(_cache) : "";
  let stopped = false;
  const check = async () => {
    const c = await readConfig();
    if (stopped || !c) return;
    _cache = c;
    const stamp = stampOf(c);
    if (stamp !== last) { last = stamp; onChange(c); }
  };
  const timer = window.setInterval(() => { void check(); }, everyMs);
  const onVisible = () => { if (document.visibilityState === "visible") void check(); };
  document.addEventListener("visibilitychange", onVisible);
  return () => { stopped = true; window.clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
}
function stampOf(c: AppConfig): string { return JSON.stringify([c.kickstarter, c.prelaunch, c.releases]); }

/** The cached env if already fetched, else null (for synchronous reads after boot). */
export function cachedEnv(): AppEnv | null { return _cache?.env ?? null; }

/** LIVE re-check of maintenance mode (uncached), so the Play buttons + /play page reflect a toggle
 *  without a full reload. Fail-safe to false (never falsely lock the game on a network blip). */
export async function fetchMaintenance(): Promise<boolean> {
  const sentAt = Date.now();
  try {
    const r = await fetch("/api/config", { cache: "no-store" });
    if (!r.ok) return false;
    const j = await r.json();
    setServerNow(j.now, sentAt);   // a free re-sync: this one is polled, so it corrects long-session drift
    return !!j.maintenance;
  } catch { return false; }
}
